import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { UserCheck, Check, X, Star, Trash2, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCoaches() {
  const [tab, setTab] = useState('applications');
  return (
    <AdminLayout title="Coaches">
      <div className="space-y-5">
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1D4ED8] text-white rounded-xl p-6">
          <h1 className="text-2xl font-bold mb-1">Coach Platform</h1>
          <p className="text-white/85 text-sm">Review applications and manage the coaches who serve clients under DCA Enterprises.</p>
        </div>
        <div className="flex gap-2 border-b border-[#E2E8F0]">
          {[['applications', 'Applications'], ['coaches', 'Coaches']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} className={`px-3 py-1.5 text-sm font-bold rounded-t ${tab === id ? 'bg-[#1D4ED8] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`} data-testid={`coach-tab-${id}`}>{label}</button>
          ))}
        </div>
        {tab === 'applications' ? <Applications /> : <Coaches />}
      </div>
    </AdminLayout>
  );
}

function Applications() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { const r = await axios.get(`${API}/api/admin/coach-applications`, { withCredentials: true }); setRows(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => { await axios.post(`${API}/api/admin/coach-applications/${id}/approve`, {}, { withCredentials: true }); toast.success('Approved — coach profile created'); load(); };
  const reject = async (id) => { await axios.post(`${API}/api/admin/coach-applications/${id}/reject`, {}, { withCredentials: true }); toast.success('Rejected'); load(); };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl divide-y" data-testid="applications-list">
      {rows.map(a => (
        <div key={a.application_id} className="p-4" data-testid={`app-row-${a.application_id}`}>
          <div className="flex items-start gap-3">
            <UserCheck size={18} className="text-[#1D4ED8] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm flex items-center gap-2">{a.name}
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${a.status === 'approved' ? 'bg-green-100 text-green-700' : a.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{a.status}</span>
              </div>
              <div className="text-xs text-[#475569]">{a.email}{a.phone ? ` · ${a.phone}` : ''}{a.years_experience ? ` · ${a.years_experience} yrs` : ''}</div>
              {a.headline && <div className="text-sm mt-1 font-medium">{a.headline}</div>}
              {a.expertise?.length > 0 && <div className="flex gap-1 flex-wrap mt-1">{a.expertise.map(e => <span key={e} className="text-[11px] bg-[#F1F5F9] px-2 py-0.5 rounded">{e}</span>)}</div>}
              {a.why && <div className="text-xs text-[#64748B] mt-1.5 line-clamp-3">{a.why}</div>}
            </div>
            {a.status === 'pending' && (
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => approve(a.application_id)} className="text-xs px-2.5 py-1.5 rounded bg-green-600 text-white inline-flex items-center gap-1" data-testid={`approve-${a.application_id}`}><Check size={12} />Approve</button>
                <button onClick={() => reject(a.application_id)} className="text-xs px-2.5 py-1.5 rounded bg-slate-200 text-slate-700 inline-flex items-center gap-1" data-testid={`reject-${a.application_id}`}><X size={12} />Reject</button>
              </div>
            )}
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="p-8 text-center text-sm text-[#94A3B8]">No applications yet.</div>}
    </div>
  );
}

function Coaches() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { const r = await axios.get(`${API}/api/admin/coaches`, { withCredentials: true }); setRows(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  const upd = async (id, patch) => { await axios.patch(`${API}/api/admin/coaches/${id}`, patch, { withCredentials: true }); load(); };
  const assign = async (coachId) => {
    const email = prompt('Client email to assign to this coach:');
    if (!email) return;
    try { await axios.post(`${API}/api/admin/coaches/${coachId}/assign-client`, { client_email: email, client_name: '' }, { withCredentials: true }); toast.success('Client assigned'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const setPrice = async (id) => {
    const p = prompt('Session price in USD (e.g. 150). Leave blank to clear:');
    if (p === null) return;
    await axios.patch(`${API}/api/admin/coaches/${id}`, { session_price: p ? parseFloat(p) : null }, { withCredentials: true }); toast.success('Saved'); load();
  };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl divide-y" data-testid="coaches-list">
      {rows.map(c => (
        <div key={c.coach_id} className="p-4 flex items-center gap-3" data-testid={`coach-row-${c.coach_id}`}>
          {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[#1D4ED8] text-white flex items-center justify-center font-bold">{c.name[0]}</div>}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm flex items-center gap-2">{c.name}
              {c.is_featured && <Star size={13} className="text-[#F97316] fill-[#F97316]" />}
              <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{c.status}</span>
            </div>
            <div className="text-xs text-[#475569] truncate">{c.headline || c.email}</div>
          </div>
          {c.status === 'active' && <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/coaches/${c.slug}`); toast.success('Profile link copied'); }} className="btn-outline text-xs"><Copy size={12} className="inline" />Link</button>}
          <button onClick={() => assign(c.coach_id)} className="btn-outline text-xs" data-testid={`assign-${c.coach_id}`}>Assign client</button>
          <button onClick={() => setPrice(c.coach_id)} className="btn-outline text-xs" data-testid={`price-${c.coach_id}`}>{c.session_price ? `$${c.session_price}` : 'Set price'}</button>
          <button onClick={() => upd(c.coach_id, { is_featured: !c.is_featured })} className="btn-outline text-xs" data-testid={`feature-${c.coach_id}`}>{c.is_featured ? 'Unfeature' : 'Feature'}</button>
          <button onClick={() => upd(c.coach_id, { status: c.status === 'active' ? 'inactive' : 'active' })} className="btn-outline text-xs">{c.status === 'active' ? 'Deactivate' : 'Activate'}</button>
        </div>
      ))}
      {rows.length === 0 && <div className="p-8 text-center text-sm text-[#94A3B8]">No coaches yet. Approve an application to create one.</div>}
    </div>
  );
}
